import { buildEgsRecord, categoryDraftFromRecord, defaultEgsRoomDraft, guestCategoryKeyForType } from './egsModel';
import { validateEgsCategory } from './egsValidation';
import { EGS_CATS } from './fields';
import type {
  EgsCategoryCompletion,
  EgsDraft,
  EgsDraftInput,
  EgsGuestCategoryDraft,
  EgsGuestCategoryKey,
  EgsRoomDraft,
} from './types';

export const EMPTY_COMPLETION: EgsCategoryCompletion = { adult: false, child: false, infant: false };

export interface WizardInitialState {
  completed: Record<string, EgsCategoryCompletion>;
  drafts: Record<string, EgsRoomDraft>;
  roomIndex: number;
}

// Editing scopes the wizard to just the one (room, category) the record belongs to, marking
// every other pair already-decided so stepping ends after this room and locks its siblings.
export function buildInitialWizardState(
  editingDraft: EgsDraft | undefined,
  roomIds: Array<string>,
): WizardInitialState {
  if (!editingDraft) {
    return {
      completed: Object.fromEntries(roomIds.map((room) => [room, { ...EMPTY_COMPLETION }])),
      drafts: Object.fromEntries(roomIds.map((room) => [room, defaultEgsRoomDraft()])),
      roomIndex: 0,
    };
  }

  const categoryKey = guestCategoryKeyForType(editingDraft.guestType);
  const representativeRoom =
    editingDraft.rooms === 'all'
      ? roomIds[0]
      : (roomIds.find((room) => (editingDraft.rooms as Array<string>).includes(room)) ?? roomIds[0]);

  const completed = Object.fromEntries(roomIds.map((room) => [room, { adult: true, child: true, infant: true }]));
  const drafts = Object.fromEntries(roomIds.map((room) => [room, defaultEgsRoomDraft()]));

  if (representativeRoom) {
    completed[representativeRoom] = { ...completed[representativeRoom], [categoryKey]: false };
    drafts[representativeRoom] = {
      ...drafts[representativeRoom],
      [categoryKey]: categoryDraftFromRecord(editingDraft),
    };
  }

  return {
    completed,
    drafts,
    roomIndex: representativeRoom ? Math.max(roomIds.indexOf(representativeRoom), 0) : 0,
  };
}

export function resolveApplyRooms(
  draft: EgsRoomDraft,
  category: EgsGuestCategoryDraft,
  currentRoom: string,
  allRooms: Array<string>,
): Array<string> {
  const mode = category.applyMode || 'this';

  if (mode === 'all') {
    return [...allRooms];
  }

  if (mode === 'particular') {
    const particular = category.applyRooms.length ? category.applyRooms : draft.globalApplyRooms;
    const list = particular.includes(currentRoom) ? [...particular] : [...particular, currentRoom];
    return list.filter((room) => allRooms.includes(room));
  }

  return [currentRoom];
}

export function normalizeRoomsValue(rooms: Array<string>, allRooms: Array<string>): 'all' | Array<string> {
  if (!rooms.length) {
    return [];
  }

  const unique = Array.from(new Set(rooms));

  if (unique.length === allRooms.length && allRooms.every((room) => unique.includes(room))) {
    return 'all';
  }

  return unique;
}

export function findNextRoomIndex(
  roomIds: Array<string>,
  completed: Record<string, EgsCategoryCompletion>,
  fromIndex: number,
): number {
  for (let index = fromIndex; index < roomIds.length; index++) {
    const room = roomIds[index];
    const done = completed[room] ?? EMPTY_COMPLETION;
    if (!done.adult || !done.child || !done.infant) {
      return index;
    }
  }
  return -1;
}

export interface ApplyRoomToPendingResult {
  error?: string;
  newRecords: Array<EgsDraftInput>;
  nextCompleted: Record<string, EgsCategoryCompletion>;
}

// Validates every enabled, undecided category on this room and builds each one's record.
// A validation failure comes back as an error string on the result, for the caller to show inline.
export function applyRoomToPending({
  audToSourceCurrencyRate,
  completed,
  currency,
  draft,
  isFxRateLoading,
  roomCompleted,
  roomId,
  roomIds,
}: {
  audToSourceCurrencyRate: Record<string, number | undefined>;
  completed: Record<string, EgsCategoryCompletion>;
  currency: string;
  draft: EgsRoomDraft;
  isFxRateLoading: boolean;
  roomCompleted: EgsCategoryCompletion;
  roomId: string;
  roomIds: Array<string>;
}): ApplyRoomToPendingResult {
  if (isFxRateLoading) {
    return {
      error: 'Still loading the exchange rate for this currency - try again in a moment.',
      newRecords: [],
      nextCompleted: completed,
    };
  }

  const rate = audToSourceCurrencyRate[currency];
  if (rate === undefined || rate <= 0) {
    return {
      error: `Could not load the exchange rate for ${currency} - refresh and try again.`,
      newRecords: [],
      nextCompleted: completed,
    };
  }

  if (draft.globalApplyMode === 'particular' && !draft.globalApplyRooms.length) {
    return {
      error: 'Select at least one room type for the section-level apply rule',
      newRecords: [],
      nextCompleted: completed,
    };
  }

  for (const meta of EGS_CATS) {
    if (roomCompleted[meta.key]) {
      continue;
    }
    const category = draft[meta.key];
    if (!category.enabled) {
      continue;
    }
    const validationError = validateEgsCategory(category, meta);
    if (validationError) {
      return { error: validationError, newRecords: [], nextCompleted: completed };
    }
  }

  const nextCompleted: Record<string, EgsCategoryCompletion> = { ...completed };
  const newRecords: Array<EgsDraftInput> = [];

  const markDone = (room: string, key: EgsGuestCategoryKey) => {
    nextCompleted[room] = { ...(nextCompleted[room] ?? EMPTY_COMPLETION), [key]: true };
  };

  EGS_CATS.forEach((meta) => {
    if (roomCompleted[meta.key]) {
      return;
    }

    const category = draft[meta.key];

    if (!category.enabled) {
      markDone(roomId, meta.key);
      return;
    }

    const targets = resolveApplyRooms(draft, category, roomId, roomIds).filter(
      (room) => !nextCompleted[room]?.[meta.key],
    );

    if (!targets.length) {
      markDone(roomId, meta.key);
      return;
    }

    const roomsValue = normalizeRoomsValue(targets, roomIds);
    newRecords.push(buildEgsRecord(category, meta, roomsValue, currency, audToSourceCurrencyRate));

    targets.forEach((room) => markDone(room, meta.key));
  });

  return { newRecords, nextCompleted };
}
